import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import TopNavBar from '../components/common/TopNavBar';
import DragMatchQuiz from '../components/quiz/DragMatchQuiz';
import LibraryQuizSessionRunner from '../components/library/LibraryQuizSessionRunner';

// ─── Inject animations once ─────────────────────────────────────────────────
let _injected = false;
const injectStyles = () => {
  if (_injected || document.getElementById('spd-styles')) return;
  _injected = true;
  const s = document.createElement('style');
  s.id = 'spd-styles';
  s.textContent = `
    @keyframes spdSlide { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }
    @keyframes shimmer  { 0%,100% { opacity:.6; } 50% { opacity:1; } }
    .spd-slide  { animation: spdSlide .35s cubic-bezier(0.34,1.56,0.64,1) both; }
    .spd-shimmer { animation: shimmer 1.4s ease-in-out infinite; }
  `;
  document.head.appendChild(s);
};

// ─── Tab config ──────────────────────────────────────────────────────────────
const TABS = [
  { key: 'vocab',    label: 'Từ vựng',       icon: '📚' },
  { key: 'grammar',  label: 'Grammar sheet', icon: '📖' },
  { key: 'hints',    label: 'Hints',         icon: '💡' },
  { key: 'quiz',     label: 'Quiz sheet',    icon: '🎯' },
];

/** Listening Flyer parts: vocabulary-by-parent view (no tabs); data keyed by part id / exercise_type. */
const isListenFlyerLibraryPart = (id) => typeof id === 'string' && id.startsWith('listen_flyer_part_');

const displayTagLabel = (tag) => {
  if (!tag?.name) return tag?.code || '—';
  if (typeof tag.name === 'object') return tag.name.vi || tag.name.en || tag.code || '—';
  return tag.name;
};

// ─── Skill color map ─────────────────────────────────────────────────────────
const SKILL_STYLES = {
  listening:       { color: '#6BB5E3', grad: 'linear-gradient(135deg,#6BB5E3,#4a90e2)', light: '#eef2ff', emoji: '🎧', label: 'Listening — Nghe' },
  reading_writing: { color: '#1E5C2E', grad: 'linear-gradient(135deg,#1E5C2E,#2a6b30)', light: '#f0fdf4', emoji: '📖', label: 'Reading & Writing — Đọc & Viết' },
  speaking:        { color: '#E8A020', grad: 'linear-gradient(135deg,#E8A020,#f99d27)', light: '#fffbeb', emoji: '🎤', label: 'Speaking — Nói' },
};

// ─── Skeleton ────────────────────────────────────────────────────────────────
const Skeleton = () => (
  <div style={{ padding: '40px 0', display: 'flex', flexDirection: 'column', gap: 16 }}>
    {[220, 120, 160, 100].map((h, i) => (
      <div key={i} className="spd-shimmer" style={{
        height: h, borderRadius: 20,
        background: 'linear-gradient(90deg,#f1f5f9 25%,#e2e8f0 50%,#f1f5f9 75%)',
      }} />
    ))}
  </div>
);

// ─── Practice helpers ────────────────────────────────────────────────────────
let _practiceStylesInjected = false;
const injectPracticeStyles = () => {
  if (_practiceStylesInjected || document.getElementById('practice-styles')) return;
  _practiceStylesInjected = true;
  const s = document.createElement('style');
  s.id = 'practice-styles';
  s.textContent = `
    @keyframes popIn   { 0%{opacity:0;transform:scale(.6)} 70%{transform:scale(1.08)} 100%{opacity:1;transform:scale(1)} }
    @keyframes shake   { 0%,100%{transform:translateX(0)} 20%,60%{transform:translateX(-8px)} 40%,80%{transform:translateX(8px)} }
    @keyframes fadeUp  { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
    @keyframes bounce  { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-10px)} }
    @keyframes starPop { 0%{opacity:0;transform:scale(0) rotate(-20deg)} 70%{transform:scale(1.2) rotate(5deg)} 100%{opacity:1;transform:scale(1) rotate(0)} }
    @keyframes confetti{ 0%{transform:translateY(-20px) rotate(0)} 100%{transform:translateY(100vh) rotate(720deg);opacity:0} }
    .pr-pop   { animation: popIn  .4s cubic-bezier(.34,1.56,.64,1) both }
    .pr-shake { animation: shake  .4s ease both }
    .pr-fadeup{ animation: fadeUp .35s ease both }
    .pr-star  { animation: starPop .5s cubic-bezier(.34,1.56,.64,1) both }
    .pr-bounce{ animation: bounce 1s ease infinite }
  `;
  document.head.appendChild(s);
};

// ─── AI Suggestion Banner (fixed bottom) ─────────────────────── 
const AISuggestionBanner = () => {
    const navigate = useNavigate();
    return (
      <section style={{
        position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 40,
        background: 'rgba(240,234,214,0.95)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
        padding: '16px 24px', borderTop: '1px solid rgba(196,160,96,0.2)', boxShadow: '0 -4px 24px rgba(94,46,26,0.1)',
      }}>
        <div style={{ maxWidth: 1152, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 16 }}>
          <span style={{ fontSize: 32, flexShrink: 0 }}>🦉</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ color: '#3E2B1E', fontWeight: 500, fontSize: 14, lineHeight: 1.5 }}>
              <strong className="font-headline" style={{ color: '#2A6B30' }}>HuPath gợi ý: </strong>
              Ôn lại <strong style={{ color: '#2A6B30' }}>Past Simple</strong> và{' '}
              <strong style={{ color: '#2A6B30' }}>Animals</strong> — 2 chủ đề cần cải thiện nhất!
            </p>
          </div>
          <button onClick={() => navigate('/skill-practice')} className="font-headline" style={{
            background: '#F99D27', color: '#fff', fontWeight: 800, padding: '10px 24px', borderRadius: 12,
            border: 'none', cursor: 'pointer', boxShadow: '0 4px 14px rgba(249,157,39,0.3)',
            display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, fontSize: 14, transition: 'all 0.2s ease', whiteSpace: 'nowrap',
          }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.03)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
          >
            Làm ngay
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>arrow_forward</span>
          </button>
        </div>
      </section>
    );
  };

// ─── RW Flyer Part 1 Practice ────────────────────────────────────────────────
const RWFlyerPart1Practice = ({ exercise, onClose }) => {
  injectPracticeStyles();
  const wordBank  = exercise.content_data?.word_bank || [];
  const instruction = exercise.content_data?.instruction || '';
  const allQs     = [...(exercise.questions || [])].sort((a, b) => a.order_index - b.order_index);
  const exampleQ  = allQs.find(q => q.points === 0);
  const practiceQs = allQs.filter(q => q.points > 0);

  const [phase, setPhase]       = useState('intro');   // intro | practice | done
  const [qIdx, setQIdx]         = useState(0);
  const [answer, setAnswer]     = useState('');
  const [checkState, setCheck]  = useState(null);      // null | correct | typo | incorrect
  const [score, setScore]       = useState(0);
  const [animKey, setAnimKey]   = useState(0);
  const [serverFeedback, setServerFeedback] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (phase === 'practice' && inputRef.current) inputRef.current.focus();
  }, [qIdx, phase]);

  const currentQ = practiceQs[qIdx];

  const checkAnswer = async () => {
    const norm = answer.trim();
    if (!norm) return;
    const token = localStorage.getItem('accessToken');
    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/client/questions/${currentQ.id}/submit`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ answer: norm, exercise_id: exercise.id }),
      }
    );
    const data = await res.json();
    if (data.is_correct) {
      setCheck('correct');
      setScore(s => s + 1);
    } else {
      setCheck(data.is_typo ? 'typo' : 'incorrect');
    }
    if (data.feedback) setServerFeedback(data.feedback);
    setAnimKey(k => k + 1);
  };

  const handleNext = () => {
    if (qIdx + 1 >= practiceQs.length) { setPhase('done'); return; }
    setQIdx(i => i + 1);
    setAnswer('');
    setCheck(null);
    setServerFeedback(null);
    setAnimKey(k => k + 1);
  };

  const handleRetry = () => { setQIdx(0); setAnswer(''); setCheck(null); setScore(0); setServerFeedback(null); setPhase('intro'); };

  // ── Word Bank strip
  const WordBank = ({ small }) => (
    <div style={{ display:'flex', flexWrap:'wrap', gap: small ? 6 : 10, justifyContent:'center', marginBottom: small ? 0 : 8 }}>
      {wordBank.map(w => (
        <span key={w} style={{
          background: 'linear-gradient(135deg,#fbbf24,#f59e0b)',
          color: '#78350f', fontWeight: 800,
          fontSize: small ? 12 : 15,
          padding: small ? '4px 10px' : '7px 18px',
          borderRadius: 50, border: '2px solid #f97316',
          boxShadow: '0 2px 6px rgba(249,115,22,0.25)',
          letterSpacing: 0.3,
        }}>{w}</span>
      ))}
    </div>
  );

  // ── Feedback box
  const FEEDBACK_CFG = {
    correct:   { bg:'#f0fdf4', border:'#86efac', color:'#166534', icon:'🎉', label:'Chính xác!' },
    typo:      { bg:'#fffbeb', border:'#fde68a', color:'#92400e', icon:'✏️', label:'Gần đúng rồi — kiểm tra chính tả nhé!' },
    incorrect: { bg:'#fff1f2', border:'#fecdd3', color:'#9f1239', icon:'💪', label:'Chưa đúng — thử lại nhé!' },
  };

  const feedbackText = checkState
    ? (serverFeedback || (currentQ?.teacher_note
        ? (checkState === 'correct'   ? currentQ.teacher_note.correct_feedback
         : checkState === 'typo'      ? currentQ.teacher_note.typo_feedback
         : currentQ.teacher_note.incorrect_feedback)
        : null))
    : null;

  // ─────────── INTRO PHASE ───────────────────────────────────────────────────
  if (phase === 'intro') return (
    <div style={{ position:'fixed', inset:0, zIndex:1000, background:'linear-gradient(160deg,#7c3aed,#6366f1,#3b82f6)', overflowY:'auto', display:'flex', flexDirection:'column', alignItems:'center', padding:'24px 16px 48px' }}>
      {/* close */}
      <div style={{ width:'100%', maxWidth:560, display:'flex', justifyContent:'flex-end', marginBottom:8 }}>
        <button onClick={onClose} style={{ background:'rgba(255,255,255,0.15)', border:'none', color:'white', borderRadius:12, padding:'8px 16px', cursor:'pointer', fontWeight:700, fontSize:13 }}>✕ Đóng</button>
      </div>

      {/* header card */}
      <div className="pr-pop" style={{ width:'100%', maxWidth:560, background:'rgba(255,255,255,0.15)', borderRadius:24, padding:'24px', marginBottom:20, textAlign:'center', backdropFilter:'blur(8px)', border:'1.5px solid rgba(255,255,255,0.25)' }}>
        <div style={{ fontSize:48, marginBottom:8 }}>📖</div>
        <h2 style={{ margin:0, color:'white', fontWeight:900, fontSize:22 }}>Reading & Writing</h2>
        <p style={{ margin:'4px 0 0', color:'rgba(255,255,255,0.8)', fontSize:14 }}>Part 1 — Matching</p>
      </div>

      {/* instruction */}
      <div className="pr-fadeup" style={{ width:'100%', maxWidth:560, background:'white', borderRadius:20, padding:'16px 20px', marginBottom:16, boxShadow:'0 4px 20px rgba(0,0,0,0.15)' }}>
        <p style={{ margin:0, color:'#1e293b', fontSize:14, lineHeight:1.6, fontWeight:500 }}>📌 {instruction}</p>
      </div>

      {/* word bank */}
      <div className="pr-fadeup" style={{ width:'100%', maxWidth:560, background:'white', borderRadius:20, padding:'16px 20px', marginBottom:16, boxShadow:'0 4px 20px rgba(0,0,0,0.15)' }}>
        <p style={{ margin:'0 0 12px', fontWeight:800, fontSize:13, color:'#64748b', textTransform:'uppercase', letterSpacing:0.5 }}>📦 Word Bank</p>
        <WordBank />
      </div>

      {/* example */}
      {exampleQ && (
        <div className="pr-fadeup" style={{ width:'100%', maxWidth:560, background:'white', borderRadius:20, padding:'16px 20px', marginBottom:24, boxShadow:'0 4px 20px rgba(0,0,0,0.15)', border:'2px solid #a3e635' }}>
          <p style={{ margin:'0 0 10px', fontSize:12, fontWeight:800, color:'#65a30d', textTransform:'uppercase', letterSpacing:0.5 }}>✅ Ví dụ (Example)</p>
          <p style={{ margin:'0 0 10px', fontSize:14, color:'#1e293b', lineHeight:1.6 }}>{exampleQ.question_text.replace(/\s*\(Example\)\s*/i,'')}</p>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <span style={{ fontSize:12, color:'#64748b' }}>Đáp án:</span>
            <span style={{ background:'#f0fdf4', border:'2px solid #86efac', borderRadius:10, padding:'4px 16px', fontWeight:800, fontSize:15, color:'#166534' }}>{exampleQ.accepted_answers?.[0]}</span>
          </div>
        </div>
      )}

      {/* start button */}
      <button
        className="pr-bounce"
        onClick={() => setPhase('practice')}
        style={{ background:'white', color:'#7c3aed', border:'none', borderRadius:20, padding:'18px 56px', fontSize:18, fontWeight:900, cursor:'pointer', boxShadow:'0 8px 32px rgba(0,0,0,0.2)', letterSpacing:0.5 }}
      >
        🚀 Bắt đầu!
      </button>
    </div>
  );

  // ─────────── PRACTICE PHASE ───────────────────────────────────────────────
  if (phase === 'practice') {
    const cfg = checkState ? FEEDBACK_CFG[checkState] : null;
    return (
      <div style={{ position:'fixed', inset:0, zIndex:1000, background:'linear-gradient(160deg,#7c3aed,#6366f1,#3b82f6)', overflowY:'auto', display:'flex', flexDirection:'column', alignItems:'center', padding:'20px 16px 48px' }}>
        {/* top bar */}
        <div style={{ width:'100%', maxWidth:560, display:'flex', alignItems:'center', gap:12, marginBottom:16 }}>
          <button onClick={onClose} style={{ background:'rgba(255,255,255,0.15)', border:'none', color:'white', borderRadius:10, padding:'7px 14px', cursor:'pointer', fontWeight:700, fontSize:12 }}>✕</button>
          <div style={{ flex:1, background:'rgba(255,255,255,0.2)', borderRadius:50, height:10, overflow:'hidden' }}>
            <div style={{ height:'100%', background:'white', borderRadius:50, width:`${((qIdx) / practiceQs.length) * 100}%`, transition:'width .4s ease' }} />
          </div>
          <span style={{ color:'white', fontWeight:800, fontSize:14, whiteSpace:'nowrap' }}>{qIdx+1}/{practiceQs.length}</span>
        </div>

        {/* word bank strip */}
        <div style={{ width:'100%', maxWidth:560, marginBottom:16 }}>
          <WordBank small />
        </div>

        {/* question card */}
        <div key={animKey} className="pr-pop" style={{ width:'100%', maxWidth:560, background:'white', borderRadius:24, padding:'28px 24px 24px', marginBottom:16, boxShadow:'0 8px 32px rgba(0,0,0,0.2)' }}>
          {/* Q number badge */}
          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:16 }}>
            <div style={{ width:36, height:36, borderRadius:12, background:'linear-gradient(135deg,#7c3aed,#6366f1)', display:'flex', alignItems:'center', justifyContent:'center', color:'white', fontWeight:900, fontSize:16 }}>
              {qIdx+1}
            </div>
            <span style={{ fontSize:12, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:0.5 }}>Câu hỏi {qIdx+1}</span>
          </div>

          <p style={{ margin:'0 0 20px', fontSize:17, fontWeight:700, color:'#1e293b', lineHeight:1.6 }}>{currentQ.question_text}</p>

          {/* input */}
          <input
            ref={inputRef}
            value={answer}
            onChange={e => { if (!checkState) setAnswer(e.target.value); }}
            onKeyDown={e => { if (e.key === 'Enter' && !checkState) checkAnswer(); }}
            disabled={!!checkState}
            placeholder="Điền câu trả lời..."
            style={{
              width:'100%', boxSizing:'border-box',
              padding:'14px 18px', fontSize:16, fontWeight:700,
              borderRadius:14, border: checkState
                ? `2.5px solid ${checkState === 'correct' ? '#86efac' : checkState === 'typo' ? '#fde68a' : '#fecdd3'}`
                : '2.5px solid #e2e8f0',
              outline:'none', color:'#1e293b',
              background: checkState === 'correct' ? '#f0fdf4' : checkState === 'typo' ? '#fffbeb' : checkState === 'incorrect' ? '#fff1f2' : 'white',
              transition:'all .2s',
            }}
          />

          {/* check button */}
          {!checkState && (
            <button
              onClick={checkAnswer}
              disabled={!answer.trim()}
              style={{
                marginTop:16, width:'100%', padding:'14px', borderRadius:14, border:'none',
                background: answer.trim() ? 'linear-gradient(135deg,#7c3aed,#6366f1)' : '#e2e8f0',
                color: answer.trim() ? 'white' : '#94a3b8',
                fontWeight:900, fontSize:16, cursor: answer.trim() ? 'pointer' : 'default',
                transition:'all .2s', boxShadow: answer.trim() ? '0 4px 16px rgba(124,58,237,0.4)' : 'none',
              }}
            >
              Kiểm tra ✓
            </button>
          )}
        </div>

        {/* feedback */}
        {cfg && (
          <div key={`fb-${animKey}`} className={checkState === 'correct' ? 'pr-pop' : 'pr-shake'} style={{ width:'100%', maxWidth:560, background:cfg.bg, border:`2px solid ${cfg.border}`, borderRadius:20, padding:'16px 20px', marginBottom:16, boxShadow:'0 4px 16px rgba(0,0,0,0.08)' }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:feedbackText ? 8 : 0 }}>
              <span style={{ fontSize:22 }}>{cfg.icon}</span>
              <span style={{ fontWeight:800, fontSize:15, color:cfg.color }}>{cfg.label}</span>
              {checkState === 'correct' && <span style={{ marginLeft:'auto', fontWeight:900, color:'#16a34a', fontSize:18 }}>+1 ⭐</span>}
            </div>
            {feedbackText && <p style={{ margin:0, fontSize:13, color:cfg.color, lineHeight:1.6 }}>{feedbackText}</p>}
            {(checkState === 'incorrect' || checkState === 'typo') && (
              <p style={{ margin:'8px 0 0', fontSize:12, color:cfg.color, fontWeight:700 }}>
                Đáp án đúng: <span style={{ background:'white', padding:'2px 10px', borderRadius:8, border:`1px solid ${cfg.border}` }}>{currentQ.accepted_answers?.[0]}</span>
              </p>
            )}
          </div>
        )}

        {/* next button */}
        {checkState && (
          <button
            key={`next-${animKey}`}
            className="pr-fadeup"
            onClick={handleNext}
            style={{ width:'100%', maxWidth:560, padding:'16px', borderRadius:16, border:'none', background:'white', color:'#7c3aed', fontWeight:900, fontSize:16, cursor:'pointer', boxShadow:'0 4px 20px rgba(0,0,0,0.15)', display:'flex', alignItems:'center', justifyContent:'center', gap:10 }}
          >
            {qIdx + 1 >= practiceQs.length ? '🏁 Xem kết quả' : 'Câu tiếp theo →'}
          </button>
        )}
      </div>
    );
  }

  // ─────────── DONE PHASE ───────────────────────────────────────────────────
  const pct = practiceQs.length > 0 ? Math.round((score / practiceQs.length) * 100) : 0;
  const stars = pct === 100 ? 3 : pct >= 60 ? 2 : pct > 0 ? 1 : 0;
  return (
    <div style={{ position:'fixed', inset:0, zIndex:1000, background:'linear-gradient(160deg,#7c3aed,#6366f1,#3b82f6)', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'24px 16px' }}>
      <div className="pr-pop" style={{ width:'100%', maxWidth:480, background:'white', borderRadius:28, padding:'36px 28px', textAlign:'center', boxShadow:'0 16px 48px rgba(0,0,0,0.25)' }}>
        {/* stars */}
        <div style={{ display:'flex', justifyContent:'center', gap:12, marginBottom:20 }}>
          {[0,1,2].map(i => (
            <span key={i} className="pr-star" style={{ fontSize:52, opacity: i < stars ? 1 : 0.2, animationDelay:`${i*0.15}s` }}>⭐</span>
          ))}
        </div>

        <h2 style={{ margin:'0 0 6px', fontWeight:900, fontSize:26, color:'#1e293b' }}>
          {pct === 100 ? 'Xuất sắc! 🎉' : pct >= 60 ? 'Làm tốt lắm! 👍' : 'Cố lên! 💪'}
        </h2>
        <p style={{ margin:'0 0 24px', fontSize:15, color:'#64748b' }}>
          Con đúng <strong style={{ color:'#7c3aed', fontSize:20 }}>{score}/{practiceQs.length}</strong> câu
        </p>

        {/* score bar */}
        <div style={{ background:'#f1f5f9', borderRadius:50, height:14, marginBottom:28, overflow:'hidden' }}>
          <div style={{ height:'100%', background:'linear-gradient(90deg,#7c3aed,#6366f1)', borderRadius:50, width:`${pct}%`, transition:'width 1s ease' }} />
        </div>

        <div style={{ display:'flex', gap:12 }}>
          <button onClick={handleRetry} style={{ flex:1, padding:'14px', borderRadius:14, border:'2.5px solid #7c3aed', background:'white', color:'#7c3aed', fontWeight:800, fontSize:15, cursor:'pointer' }}>
            🔄 Làm lại
          </button>
          <button onClick={onClose} style={{ flex:1, padding:'14px', borderRadius:14, border:'none', background:'linear-gradient(135deg,#7c3aed,#6366f1)', color:'white', fontWeight:800, fontSize:15, cursor:'pointer', boxShadow:'0 4px 16px rgba(124,58,237,0.4)' }}>
            ✓ Xong
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Quiz tab ────────────────────────────────────────────────────────────────
const QUIZ_PER_PAGE = 10;

const QUIZ_TYPE_LABEL = {
  single_choice_text: 'Chọn 1 đáp án',
  single_choice_image: 'Chọn hình',
  multiple_choice_text: 'Chọn nhiều đáp án',
  multiple_choice_image: 'Chọn nhiều hình',
  fill_in_the_blank: 'Điền vào chỗ trống',
  matching: 'Nối cặp',
  drag_and_drop: 'Kéo thả',
  ordering: 'Sắp xếp',
  true_false: 'Đúng/Sai',
  short_answer: 'Trả lời ngắn',
  listening_single_choice: 'Nghe + chọn đáp án',
  listening_matching: 'Nghe + nối cặp',
  listening_drag_zone: 'Nghe + kéo vào vùng',
  listening_fill_blank: 'Nghe + điền từ',
};

const QUIZ_TYPE_COLOR = {
  single_choice_text: '#6366f1',
  single_choice_image: '#8b5cf6',
  multiple_choice_text: '#f59e0b',
  multiple_choice_image: '#f97316',
  fill_in_the_blank: '#06b6d4',
  matching: '#10b981',
  drag_and_drop: '#3b82f6',
  ordering: '#ec4899',
  true_false: '#84cc16',
  short_answer: '#64748b',
  listening_single_choice: '#6366f1',
  listening_matching: '#10b981',
  listening_drag_zone: '#0d9488',
  listening_fill_blank: '#06b6d4',
};

const QuizTab = ({ exerciseType }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setLoading(true);
    setError(null);
    const token = localStorage.getItem('accessToken');
    const skip = (page - 1) * QUIZ_PER_PAGE;
    fetch(
      `${process.env.REACT_APP_API_URL}/quizzes/pending?exercise_type=${encodeURIComponent(exerciseType)}&skip=${skip}&limit=${QUIZ_PER_PAGE}`,
      token ? { headers: { Authorization: `Bearer ${token}` } } : {}
    )
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(json => { setData(json); setLoading(false); })
      .catch(err => { setError(err); setLoading(false); });
  }, [exerciseType, page]);

  if (loading) return (
    <div style={{ padding: '40px 0', textAlign: 'center', color: '#94a3b8' }}>Đang tải quiz...</div>
  );

  if (error) return (
    <div style={{ padding: '40px 0', textAlign: 'center', color: '#ef4444' }}>
      Không tải được danh sách quiz. {error === 401 || error === 403 ? '(Cần đăng nhập)' : ''}
    </div>
  );

  const quizzes = data?.data || [];
  const count = data?.count || 0;
  const totalPages = Math.ceil(count / QUIZ_PER_PAGE);

  if (count === 0) return (
    <div style={{ textAlign: 'center', padding: '64px 24px', color: '#94a3b8' }}>
      <div style={{ fontSize: 56, marginBottom: 12 }}>🎉</div>
      <p style={{ fontWeight: 700, fontSize: 16, margin: 0 }}>Chưa có quiz nào cho phần này.</p>
    </div>
  );

  const mediaBase = (process.env.REACT_APP_API_URL || '').replace('/api/v1', '');

  return (
    <div style={{ padding: '24px 0', display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Header */}
      <div style={{ fontSize: 13, color: '#64748b', fontWeight: 600, marginBottom: 4 }}>
        {count} quiz · Trang {page}/{totalPages}
      </div>

      {/* Quiz cards */}
      {quizzes.map((quiz, idx) => {
        const color = QUIZ_TYPE_COLOR[quiz.quiz_type] || '#6366f1';
        const contextText = quiz.context?.context_text || '';
        const contextImageUrl = quiz.context?.context_image_url || '';
        const questionText = quiz.question?.question_text || '';
        const options = quiz.options || [];
        const globalIdx = (page - 1) * QUIZ_PER_PAGE + idx + 1;

        return (
          <div key={quiz.id} style={{
            background: 'white', borderRadius: 16, border: `2px solid ${color}22`,
            padding: '16px 20px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              {/* Index badge */}
              <div style={{
                width: 32, height: 32, borderRadius: 10, flexShrink: 0,
                background: color, display: 'flex', alignItems: 'center',
                justifyContent: 'center', color: 'white', fontWeight: 800, fontSize: 13,
              }}>
                {globalIdx}
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                {/* Quiz type badge */}
                <div style={{ marginBottom: 10 }}>
                  <span style={{
                    fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 8,
                    background: `${color}18`, color, textTransform: 'uppercase', letterSpacing: 0.5,
                  }}>
                    {QUIZ_TYPE_LABEL[quiz.quiz_type] || quiz.quiz_type || '—'}
                  </span>
                </div>

                {/* Context text */}
                {contextText && (
                  <div style={{
                    marginBottom: 10, padding: '10px 14px', borderRadius: 10,
                    background: '#f0fdf4', border: '1px solid #bbf7d0',
                  }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#166534', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                      Context
                    </div>
                    <p style={{ margin: 0, fontSize: 13, color: '#1e293b', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                      {contextText}
                    </p>
                  </div>
                )}

                {/* Drag-match quiz (has context image + persons annotations) */}
                {contextImageUrl && quiz.context?.persons?.length > 0 ? (
                  <div style={{ marginBottom: 10 }}>
                    <DragMatchQuiz quiz={quiz} mediaBase={mediaBase} />
                  </div>
                ) : (
                  <>
                    {/* Context image (plain) */}
                    {contextImageUrl && (
                      <div style={{ marginBottom: 10 }}>
                        <img
                          src={`${mediaBase}${contextImageUrl}`}
                          alt="context"
                          style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 10, objectFit: 'contain' }}
                        />
                      </div>
                    )}

                    {/* Question */}
                    {questionText && (
                      <p style={{ margin: '0 0 10px', fontSize: 14, fontWeight: 600, color: '#1e293b', lineHeight: 1.5 }}>
                        {questionText}
                      </p>
                    )}
                  </>
                )}

                {/* Options */}
                {options.length > 0 && !(contextImageUrl && quiz.context?.persons?.length > 0) && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
                    {options.map((opt, i) => (
                      <div key={opt.id ?? i} style={{
                        padding: '8px 14px', borderRadius: 10,
                        background: '#f8fafc', border: '1.5px solid #e2e8f0',
                        fontSize: 13, color: '#334155', display: 'flex', alignItems: 'center', gap: 8,
                      }}>
                        <span style={{ fontWeight: 700, color: '#94a3b8', flexShrink: 0 }}>
                          {opt.id || String.fromCharCode(65 + i)}.
                        </span>
                        {opt.image_url ? (
                          <img src={`${mediaBase}${opt.image_url}`} alt={opt.text || ''} style={{ maxHeight: 60, borderRadius: 6 }} />
                        ) : (
                          <span>{opt.text}</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Tags grouped by tag type */}
                {quiz.tags?.length > 0 && (() => {
                  const grouped = quiz.tags.reduce((acc, t) => {
                    const key = t.tag_type_name;
                    if (!acc[key]) acc[key] = [];
                    acc[key].push(t);
                    return acc;
                  }, {});
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
                      {Object.entries(grouped).map(([typeName, tags]) => (
                        <div key={typeName}>
                          <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 5 }}>
                            {typeName}
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                            {tags.map(t => (
                              <span key={t.tag_id} style={{
                                fontSize: 11, fontWeight: 600,
                                padding: '3px 10px', borderRadius: 20,
                                background: '#f1f5f9', border: '1px solid #e2e8f0',
                                color: '#475569',
                              }}>
                                {t.tag_name}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        );
      })}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 8 }}>
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            style={{
              padding: '7px 16px', borderRadius: 10, border: '1.5px solid #e2e8f0',
              background: page === 1 ? '#f8fafc' : 'white', color: page === 1 ? '#cbd5e1' : '#475569',
              fontWeight: 700, fontSize: 13, cursor: page === 1 ? 'default' : 'pointer',
            }}
          >
            ← Trước
          </button>

          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
            .reduce((acc, p, i, arr) => {
              if (i > 0 && p - arr[i - 1] > 1) acc.push('...');
              acc.push(p);
              return acc;
            }, [])
            .map((p, i) => p === '...' ? (
              <span key={`ellipsis-${i}`} style={{ color: '#94a3b8', fontSize: 13 }}>…</span>
            ) : (
              <button
                key={p}
                onClick={() => setPage(p)}
                style={{
                  width: 34, height: 34, borderRadius: 10, border: '1.5px solid',
                  borderColor: p === page ? '#6366f1' : '#e2e8f0',
                  background: p === page ? '#6366f1' : 'white',
                  color: p === page ? 'white' : '#475569',
                  fontWeight: 700, fontSize: 13, cursor: 'pointer',
                }}
              >
                {p}
              </button>
            ))}

          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            style={{
              padding: '7px 16px', borderRadius: 10, border: '1.5px solid #e2e8f0',
              background: page === totalPages ? '#f8fafc' : 'white', color: page === totalPages ? '#cbd5e1' : '#475569',
              fontWeight: 700, fontSize: 13, cursor: page === totalPages ? 'default' : 'pointer',
            }}
          >
            Tiếp →
          </button>
        </div>
      )}
    </div>
  );
};

// ─── Empty placeholder tab ───────────────────────────────────────────────────
const ComingSoon = ({ icon, label }) => (
  <div style={{ textAlign: 'center', padding: '64px 24px', color: '#94a3b8' }}>
    <div style={{ fontSize: 56, marginBottom: 12 }}>{icon}</div>
    <p style={{ fontWeight: 700, fontSize: 16, margin: 0 }}>Mục <strong>{label}</strong> đang được xây dựng</p>
    <p style={{ fontSize: 13, marginTop: 4 }}>Sẽ sớm có ngay nhé! 🚀</p>
  </div>
);

// ─── Vocab tab ──────────────────────────────────────────────────────────────
const VocabTab = ({ vocabularies }) => {
  const [expandedCategories, setExpandedCategories] = useState({});

  const toggleCategory = (id) => {
    setExpandedCategories(prev => ({ ...prev, [id]: !prev[id] }));
  };

  if (!vocabularies || vocabularies.length === 0) {
    return <ComingSoon icon="📚" label="Từ vựng" />;
  }

  return (
    <div style={{ padding: '24px 0', display: 'flex', flexDirection: 'column', gap: 16 }}>
      {vocabularies.map((category) => {
        const isExpanded = expandedCategories[category.id] !== false; // default expanded
        const patternCount = category.voca_patterns?.length || 0;
        return (
          <div key={category.id} style={{
            background: 'white', borderRadius: 20, border: '2px solid #fde68a',
            overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          }}>
            {/* Category header */}
            <button
              onClick={() => toggleCategory(category.id)}
              style={{
                width: '100%', padding: '16px 20px',
                background: 'linear-gradient(135deg, #fffbeb, #fef3c7)',
                borderBottom: isExpanded ? '2px solid #fde68a' : 'none',
                border: 'none', cursor: 'pointer', textAlign: 'left',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}
            >
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: '#92400e', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 12, transition: 'transform .2s', transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)', display: 'inline-block' }}>▶</span>
                <span>📂</span>
                {category.titles?.vi || category.titles?.en || category.code}
              </h3>
              <span style={{
                background: '#fbbf24', color: '#78350f', fontSize: 11, fontWeight: 800,
                padding: '2px 10px', borderRadius: 12, flexShrink: 0,
              }}>
                {patternCount} patterns
              </span>
            </button>

            {/* Voca patterns list */}
            {isExpanded && patternCount > 0 && (
              <div style={{ padding: '12px 20px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
                {category.voca_patterns.map((vp) => (
                  <div key={vp.id} style={{
                    background: '#fffbeb', border: '1.5px solid #fde68a',
                    borderRadius: 12, overflow: 'hidden',
                  }}>
                    {/* Pattern header */}
                    <div style={{ padding: '10px 14px', borderBottom: vp.related_categories?.length ? '1px solid #fde68a' : 'none' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: vp.formula ? 4 : 0 }}>
                        <span style={{
                          background: '#fbbf24', color: '#78350f',
                          fontSize: 10, fontWeight: 800, padding: '2px 8px', borderRadius: 8, flexShrink: 0,
                        }}>
                          {vp.code}
                        </span>
                      </div>
                      {vp.formula && (
                        <p style={{ margin: 0, fontSize: 13, color: '#92400e', fontFamily: 'monospace', lineHeight: 1.5 }}>
                          {vp.formula_show || vp.formula}
                        </p>
                      )}
                      {vp.content?.vi?.usage && (
                        <p style={{ margin: '4px 0 0', fontSize: 12, color: '#b45309', lineHeight: 1.4 }}>
                          {vp.content.vi.usage}
                        </p>
                      )}
                      {vp.content?.en?.usage && (
                        <p style={{ margin: '2px 0 0', fontSize: 12, color: '#6b7280', lineHeight: 1.4, fontStyle: 'italic' }}>
                          {vp.content.en.usage}
                        </p>
                      )}
                    </div>

                    {/* Key output examples */}
                    {vp.key_output?.length > 0 && (
                      <div style={{
                        padding: '8px 14px', borderBottom: vp.related_categories?.length ? '1px solid #fde68a' : 'none',
                        background: '#f0fdf4',
                      }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: '#166534', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          Key output
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                          {vp.key_output.map((ex, idx) => (
                            <div key={idx} style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                              <span style={{ fontSize: 10, color: '#86efac', fontWeight: 700, flexShrink: 0 }}>{idx + 1}.</span>
                              <span style={{ fontSize: 12, color: '#15803d', fontStyle: 'italic' }}>{ex}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Related categories — 1 row each */}
                    {vp.related_categories?.map((rc) => (
                      <div key={rc.id} style={{
                        display: 'flex', alignItems: 'flex-start', gap: 12,
                        padding: '8px 14px', borderBottom: '1px solid #fef3c7',
                      }}>
                        {/* Category label */}
                        <span style={{
                          flexShrink: 0, fontSize: 11, fontWeight: 700,
                          color: '#b45309', background: '#fef3c7',
                          padding: '2px 8px', borderRadius: 6, marginTop: 2,
                          minWidth: 80, textAlign: 'center',
                        }}>
                          {rc.titles?.vi || rc.titles?.en || rc.code.split('-')[0]}
                        </span>
                        {/* Vocabulary chips */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {rc.vocabularies?.map((vocab) => (
                            <span key={vocab.id} style={{
                              background: 'white', border: '1px solid #fde68a',
                              borderRadius: 8, padding: '2px 10px',
                              fontSize: 12, color: '#92400e', fontWeight: 600,
                            }}>
                              {vocab.word}
                              {vocab.pos && (
                                <span style={{ fontWeight: 400, color: '#b45309', fontSize: 10, marginLeft: 3, fontStyle: 'italic' }}>
                                  ({vocab.pos})
                                </span>
                              )}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ─── Grammar tab ────────────────────────────────────────────────────────────
const GrammarTab = ({ grammars }) => {
  if (!grammars || grammars.length === 0) {
    return <ComingSoon icon="📖" label="Ngữ pháp" />;
  }

  return (
    <div style={{ padding: '24px 0', display: 'flex', flexDirection: 'column', gap: 12 }}>
      {grammars.map((grammar, i) => (
        <div key={grammar.id} style={{
          background: 'white', borderRadius: 16, border: '2px solid #c7d2fe',
          padding: '16px 20px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          transition: 'transform .15s, box-shadow .15s',
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10, flexShrink: 0,
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'white', fontWeight: 800, fontSize: 14,
            }}>
              {i + 1}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h4 style={{ margin: '0 0 6px', fontWeight: 800, fontSize: 15, color: '#3730a3' }}>
                {grammar.name?.vi || grammar.name?.en || grammar.code}
              </h4>
              {grammar.name?.en && grammar.name?.vi && (
                <p style={{ margin: '0 0 6px', fontSize: 13, color: '#6366f1', fontStyle: 'italic' }}>
                  {grammar.name.en}
                </p>
              )}
              <div style={{
                background: '#eef2ff', borderRadius: 10, padding: '8px 14px', marginBottom: 8,
                fontFamily: 'monospace', fontSize: 13, color: '#4338ca', fontWeight: 600,
                lineHeight: 1.5, wordBreak: 'break-word',
              }}>
                {grammar.formula}
              </div>
              {grammar.content?.vi?.description && (
                <p style={{ margin: '0 0 6px', fontSize: 13, color: '#64748b', lineHeight: 1.5 }}>
                  {grammar.content.vi.description}
                </p>
              )}
              {grammar.content?.en?.description && (
                <p style={{ margin: '0 0 6px', fontSize: 13, color: '#94a3b8', fontStyle: 'italic', lineHeight: 1.5 }}>
                  {grammar.content.en.description}
                </p>
              )}
              {(grammar.content?.vi?.example || grammar.content?.en?.example) && (
                <div style={{
                  background: '#f0fdf4', borderRadius: 10, padding: '8px 14px', marginTop: 4,
                  border: '1px solid #bbf7d0',
                }}>
                  {grammar.content?.vi?.example && (
                    <p style={{ margin: 0, fontSize: 13, color: '#16a34a', lineHeight: 1.5 }}>
                      📝 {grammar.content.vi.example}
                    </p>
                  )}
                  {grammar.content?.en?.example && (
                    <p style={{ margin: '4px 0 0', fontSize: 13, color: '#4ade80', fontStyle: 'italic', lineHeight: 1.5 }}>
                      {grammar.content.en.example}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const HintTab = ({ hints }) => {
  if (!hints || hints.length === 0) {
    return <ComingSoon icon="💡" label="Hints" />;
  }

  return (
    <div style={{ padding: '24px 0', display: 'flex', flexDirection: 'column', gap: 12 }}>
      {hints.map((hint, i) => (
        <div key={hint.id} style={{
          background: 'white', borderRadius: 16, border: '2px solid #fde68a',
          padding: '16px 20px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          transition: 'transform .15s, box-shadow .15s',
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10, flexShrink: 0,
              background: 'linear-gradient(135deg, #f59e0b, #f97316)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'white', fontWeight: 800, fontSize: 14,
            }}>
              {i + 1}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h4 style={{ margin: '0 0 6px', fontWeight: 800, fontSize: 15, color: '#92400e' }}>
                {hint.name?.vi || hint.name?.en || hint.code}
              </h4>
              {hint.name?.en && hint.name?.vi && (
                <p style={{ margin: '0 0 6px', fontSize: 13, color: '#d97706', fontStyle: 'italic' }}>
                  {hint.name.en}
                </p>
              )}
              {hint.formula && (
                <div style={{
                  background: '#fffbeb', borderRadius: 10, padding: '8px 14px', marginBottom: 8,
                  fontFamily: 'monospace', fontSize: 13, color: '#b45309', fontWeight: 600,
                  lineHeight: 1.5, wordBreak: 'break-word',
                }}>
                  {hint.formula}
                </div>
              )}
              {hint.content?.vi?.description && (
                <p style={{ margin: '0 0 6px', fontSize: 13, color: '#64748b', lineHeight: 1.5 }}>
                  {hint.content.vi.description}
                </p>
              )}
              {hint.content?.en?.description && (
                <p style={{ margin: '0 0 6px', fontSize: 13, color: '#94a3b8', fontStyle: 'italic', lineHeight: 1.5 }}>
                  {hint.content.en.description}
                </p>
              )}
              {(hint.content?.vi?.example || hint.content?.en?.example) && (
                <div style={{
                  background: '#fefce8', borderRadius: 10, padding: '8px 14px', marginTop: 4,
                  border: '1px solid #fde68a',
                }}>
                  {hint.content?.vi?.example && (
                    <p style={{ margin: 0, fontSize: 13, color: '#a16207', lineHeight: 1.5 }}>
                      💡 {hint.content.vi.example}
                    </p>
                  )}
                  {hint.content?.en?.example && (
                    <p style={{ margin: '4px 0 0', fontSize: 13, color: '#ca8a04', fontStyle: 'italic', lineHeight: 1.5 }}>
                      {hint.content.en.example}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── Listening Flyer parts: tags from vocabularies.tag_code, grouped by parent in tag_links ─
const ExerciseVocabularyByParentSection = ({ groups, loading, error, accentColor }) => {
  if (loading) {
    return (
      <div style={{ padding: '40px 0', textAlign: 'center', color: '#94a3b8' }}>Đang tải từ vựng...</div>
    );
  }
  if (error) {
    return (
      <div style={{ padding: '40px 0', textAlign: 'center', color: '#ef4444' }}>
        Không tải được nhóm từ vựng theo chủ đề.
      </div>
    );
  }
  if (!groups.length) {
    return (
      <div style={{ textAlign: 'center', padding: '48px 24px', color: '#94a3b8' }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>🏷️</div>
        <p style={{ fontWeight: 700, margin: 0 }}>Chưa có tag vocabulary nào trên quiz của phần này.</p>
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {groups.map((group, gi) => {
        const parent = group.parent;
        const parentKey = parent?.id ?? `orphan-${gi}`;
        const parentLabel = parent ? displayTagLabel(parent) : 'Không gắn nhóm cha (tag_links)';
        return (
          <div
            key={parentKey}
            className="rounded-xl border border-gray-100 bg-white p-5 shadow-[0px_8px_24px_rgba(94,46,26,0.06)]"
            style={{ borderTop: `4px solid ${accentColor}` }}
          >
            <div style={{ marginBottom: 14 }}>
              <div className="font-headline font-black text-hp-on-surface" style={{ fontSize: 17, marginBottom: 4 }}>
                {parentLabel}
              </div>
              {parent?.code && (
                <div style={{ fontSize: 12, fontWeight: 600, color: '#94a3b8', fontFamily: 'monospace' }}>
                  {parent.code}
                </div>
              )}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 }}>
              {(group.vocabulary_tags || []).map((item) => {
                const tag = item.tag || item;
                const headword = item.word?.trim() || displayTagLabel(tag);
                const meaning = (item.meaning && String(item.meaning).trim()) || '';
                return (
                  <div
                    key={tag.id}
                    className="rounded-lg border border-gray-100 bg-slate-50/80 p-3"
                  >
                    <div className="font-headline font-bold text-hp-on-surface" style={{ fontSize: 14, lineHeight: 1.35 }}>
                      {headword}
                    </div>
                    {meaning ? (
                      <div style={{ fontSize: 12, fontWeight: 500, color: '#64748b', marginTop: 6, lineHeight: 1.45 }}>
                        {meaning}
                      </div>
                    ) : (
                      <div style={{ fontSize: 11, fontWeight: 500, color: '#94a3b8', marginTop: 6, fontStyle: 'italic' }}>
                        Chưa có nghĩa trong từ điển
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────
const LibraryPartDetailPage = () => {
  injectStyles();
  const { partId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('vocab');
  const [content, setContent] = useState(null);
  const [contentLoading, setContentLoading] = useState(true);

  const [part, setPart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [practiceExercise, setPracticeExercise] = useState(null);
  const [practiceLoading, setPracticeLoading] = useState(false);
  const [practiceError, setPracticeError] = useState(null);

  const [vocabByParentGroups, setVocabByParentGroups] = useState([]);
  const [vocabGroupsLoading, setVocabGroupsLoading] = useState(false);
  const [vocabGroupsError, setVocabGroupsError] = useState(null);

  const [quizSessionOpen, setQuizSessionOpen] = useState(false);

  const isListenFlyerVocabGroupsView = isListenFlyerLibraryPart(partId);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      setContentLoading(true);
      setError(null);
      if (!isListenFlyerVocabGroupsView) {
        setVocabByParentGroups([]);
        setVocabGroupsError(null);
      }
      try {
        const metaRes = await fetch(`${process.env.REACT_APP_API_URL}/exercises/exercise-types`);
        if (metaRes.ok) {
          const metaData = await metaRes.json();
          const found = (metaData.data || metaData).find(item => item.id === partId);
          if (found) setPart(found);
          else setError(new Error('Part not found'));
        } else {
          setError(new Error('Failed to load metadata'));
        }

        if (isListenFlyerVocabGroupsView) {
          setContent(null);
          setVocabGroupsLoading(true);
          setVocabGroupsError(null);
          const groupsRes = await fetch(
            `${process.env.REACT_APP_API_URL}/tags/exercise-vocabulary-by-parent?exercise_type=${encodeURIComponent(partId)}`
          );
          if (groupsRes.ok) {
            const groups = await groupsRes.json();
            setVocabByParentGroups(Array.isArray(groups) ? groups : []);
          } else {
            setVocabByParentGroups([]);
            setVocabGroupsError(new Error('vocab_groups_fetch_failed'));
          }
          setVocabGroupsLoading(false);
        } else {
          const contentRes = await fetch(
            `${process.env.REACT_APP_API_URL}/exercises/exercise-types/${partId}/content`
          );
          if (contentRes.ok) {
            const contentData = await contentRes.json();
            setContent(contentData);
          }
        }
      } catch (e) {
        console.error('Failed to fetch data', e);
        setError(e);
        if (isListenFlyerVocabGroupsView) {
          setVocabGroupsError(e);
          setVocabByParentGroups([]);
        }
      } finally {
        setLoading(false);
        setContentLoading(false);
        if (isListenFlyerVocabGroupsView) setVocabGroupsLoading(false);
      }
    };
    fetchAll();
  }, [partId, isListenFlyerVocabGroupsView]);

  // Determine skill style from part data
  const skillKey = part?.skill?.toLowerCase().replace(/\s+&\s+|\s+/g, '_') || '';
  const style = SKILL_STYLES[skillKey] || SKILL_STYLES['listening'];

  const renderTab = () => {
    if (isListenFlyerVocabGroupsView) return null;
    if (contentLoading && (activeTab === 'vocab' || activeTab === 'grammar' || activeTab === 'hints')) {
      return <div style={{ padding: '40px 0', textAlign: 'center', color: '#94a3b8' }}>Đang tải...</div>;
    }
    switch (activeTab) {
      case 'vocab':   return <VocabTab vocabularies={content?.vocabularies} />;
      case 'grammar': return <GrammarTab grammars={content?.grammars} />;
      case 'hints':   return <HintTab hints={content?.hints} />;
      case 'quiz':    return <QuizTab exerciseType={partId} />;
      case 'mock':    return <ComingSoon icon="🏆" label="Thi thử" />;
      default:        return null;
    }
  };

  return (
    <div className="min-h-screen ob-sky-voyager font-body text-hp-on-surface relative overflow-x-hidden hp-trail-pattern">
      <TopNavBar />

      <main className="max-w-6xl mx-auto px-6 py-8 relative z-10">

        {loading && <Skeleton />}

        {error && (
          <div className="bg-white rounded-xl shadow-[0px_8px_24px_rgba(94,46,26,0.06)] p-12 text-center">
            <div style={{ fontSize: 48 }}>😕</div>
            <p className="text-hp-on-surface/60 font-medium mt-4">Không tải được thông tin part.</p>
            <button
              onClick={() => navigate(-1)}
              className="mt-6 px-8 py-3 rounded-xl bg-hupath-scout text-white font-black font-headline shadow-lg hover:scale-105 transition-transform"
            >
              ← Quay lại
            </button>
          </div>
        )}

        {!loading && !error && part && (
          <div className="space-y-6">

            {isListenFlyerVocabGroupsView && (
              <section
                className="rounded-xl p-5 md:p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4 font-headline"
                style={{
                  background: `linear-gradient(135deg, ${style.color}18, ${style.light})`,
                  border: `2px solid ${style.color}44`,
                  boxShadow: '0px 8px 24px rgba(94,46,26,0.06)',
                }}
              >
                <div className="min-w-0">
                  <div className="text-xs font-black uppercase tracking-wider mb-1" style={{ color: style.color }}>
                    Luyện quiz
                  </div>
                  <h2 className="text-lg md:text-xl font-black text-hp-on-surface leading-snug">
                    Chuỗi nhanh: tối đa 5 quiz ngẫu nhiên
                  </h2>
                  <p className="text-sm text-hp-on-surface/60 font-medium mt-1">
                    Chọn ngẫu nhiên tối đa 5 quiz của phần này (có thể gồm câu đã làm). Làm liên tục, chấm từng câu; cuối cùng xem tổng kết (cần đăng nhập).
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setQuizSessionOpen(true)}
                  className="shrink-0 px-8 py-3.5 rounded-xl text-white font-black text-sm shadow-lg hover:scale-[1.02] transition-transform flex items-center justify-center gap-2"
                  style={{ background: style.color }}
                >
                  <span>🎯</span> Bắt đầu
                </button>
              </section>
            )}

            {/* ── Header Card (Matching LibraryPage style) ──────────────── */}
            <section className="bg-white rounded-xl shadow-[0px_8px_24px_rgba(94,46,26,0.06)] p-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                <div className="flex items-center gap-4">
                    <div style={{
                        width: 64, height: 64, borderRadius: 16, flexShrink: 0,
                        background: style.light,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 32,
                    }}>
                        {style.emoji}
                    </div>
                    <div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: style.color, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 2 }}>
                            {style.label}
                        </div>
                        <h1 className="text-2xl md:text-3xl font-black text-hp-on-surface font-headline leading-tight">
                            {part.name}
                        </h1>
                    </div>
                </div>

                <div className="flex flex-wrap gap-3">
                    <button
                        onClick={() => navigate(-1)}
                        className="px-6 py-2.5 rounded-xl border-2 border-gray-100 text-hp-on-surface/60 font-bold font-headline text-sm hover:bg-gray-50 transition-colors"
                    >
                        ← Quay lại
                    </button>
                    <button
                        onClick={async () => {
                            setPracticeLoading(true);
                            setPracticeError(null);
                            setPracticeExercise(null);
                            try {
                              const token = localStorage.getItem('accessToken');
                              const res = await fetch(
                                `${process.env.REACT_APP_API_URL}/exercises/random?exercise_type=${encodeURIComponent(partId)}`,
                                token ? { headers: { Authorization: `Bearer ${token}` } } : {}
                              );
                              if (!res.ok) throw new Error(`HTTP ${res.status}`);
                              const data = await res.json();
                              setPracticeExercise(data);
                            } catch (e) {
                              setPracticeError(e.message);
                            } finally {
                              setPracticeLoading(false);
                            }
                        }}
                        disabled={practiceLoading}
                        className="px-8 py-2.5 rounded-xl bg-hupath-scout text-white font-black font-headline text-sm shadow-lg hover:scale-105 transition-transform flex items-center gap-2"
                    >
                        {practiceLoading ? '⏳' : '🚀'} {practiceLoading ? 'Đang tải...' : 'Luyện tập ngay'}
                    </button>
                </div>
              </div>
              
              {part.description && (
                <p className="mt-4 text-hp-on-surface/60 font-medium text-sm border-t border-gray-50 pt-4">
                  {part.description}
                </p>
              )}

              {/* Quick stats row */}
              {!isListenFlyerVocabGroupsView ? (
                <div className="flex flex-wrap gap-4 mt-6">
                  {[
                    { icon: '📚', label: 'Patterns', value: content?.vocabularies?.reduce((sum, cat) => sum + (cat.voca_patterns?.length || 0), 0) ?? '—' },
                    { icon: '📖', label: 'Grammar', value: content?.grammars?.length ?? '—' },
                    { icon: '💡', label: 'Hints', value: content?.hints?.length ?? '—' },
                    { icon: '🎯', label: 'Quiz', value: part.quiz_count ?? '—' },
                  ].map(stat => (
                    <div key={stat.label} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-50 border border-gray-100">
                      <span className="text-lg">{stat.icon}</span>
                      <span className="font-black text-hp-on-surface">{stat.value}</span>
                      <span className="text-[10px] font-bold text-hp-on-surface/40 uppercase tracking-wider">{stat.label}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-wrap gap-4 mt-6">
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-50 border border-gray-100">
                    <span className="text-lg">📚</span>
                    <span className="font-black text-hp-on-surface">
                      {vocabGroupsLoading
                        ? '—'
                        : vocabByParentGroups.reduce((n, g) => n + (g.vocabulary_tags?.length || 0), 0)}
                    </span>
                    <span className="text-[10px] font-bold text-hp-on-surface/40 uppercase tracking-wider">
                      Tag (vocabularies)
                    </span>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-50 border border-gray-100">
                    <span className="text-lg">🏷️</span>
                    <span className="font-black text-hp-on-surface">
                      {vocabGroupsLoading ? '—' : vocabByParentGroups.length}
                    </span>
                    <span className="text-[10px] font-bold text-hp-on-surface/40 uppercase tracking-wider">
                      Nhóm theo cha
                    </span>
                  </div>
                </div>
              )}
            </section>

            {/* ── Tab Navigation (hidden for listen_flyer_part_* vocabulary groups view) ─ */}
            {!isListenFlyerVocabGroupsView && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {TABS.map(tab => {
                  const isActive = activeTab === tab.key;
                  return (
                    <button
                      key={tab.key}
                      onClick={() => setActiveTab(tab.key)}
                      className={`p-4 rounded-xl shadow-sm font-headline flex items-center justify-center gap-3 transition-all ${
                          isActive
                          ? 'bg-hupath-scout text-white shadow-md scale-[1.02]'
                          : 'bg-white text-hp-on-surface/60 hover:bg-gray-50'
                      }`}
                    >
                      <span className="text-2xl">{tab.icon}</span>
                      <span className="font-black text-sm uppercase tracking-wide">
                        {tab.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}

            {/* ── Tab / topic content ───────────────────────────────────── */}
            <div
              key={isListenFlyerVocabGroupsView ? `vocab-groups-${partId}` : activeTab}
              className="spd-slide pb-32"
            >
              {isListenFlyerVocabGroupsView ? (
                <section className="bg-white rounded-xl shadow-[0px_8px_24px_rgba(94,46,26,0.06)] p-6">
                  <h2 className="font-headline font-black text-lg text-hp-on-surface mb-1">
                    Từ vựng theo nhóm chủ đề (tag cha)
                  </h2>
                  <p className="text-sm text-hp-on-surface/55 font-medium mb-6">
                    Tags khớp mã trên bảng vocabularies của part này, gom theo tag cha trong tag_links: tiêu đề là cha, ô bên dưới là các tag con.
                  </p>
                  <ExerciseVocabularyByParentSection
                    groups={vocabByParentGroups}
                    loading={vocabGroupsLoading}
                    error={vocabGroupsError}
                    accentColor={style.color}
                  />
                </section>
              ) : (
                renderTab()
              )}
            </div>

          </div>
        )}
      </main>

      <AISuggestionBanner />

      {/* Background decorations */}
      <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[150px] bg-white/60 blur-[60px] rounded-[100%] pointer-events-none -z-10"></div>
      <div className="fixed top-20 right-[-100px] w-64 h-64 bg-amber-200/20 blur-[80px] rounded-full pointer-events-none -z-10"></div>
      <div className="fixed bottom-20 left-[-100px] w-80 h-80 bg-hupath-scout/10 blur-[100px] rounded-full pointer-events-none -z-10"></div>

      {/* ── Practice overlay ──────────────────────────────────────────── */}
      {practiceExercise && (
        <RWFlyerPart1Practice
          exercise={practiceExercise}
          onClose={() => setPracticeExercise(null)}
        />
      )}

      {isListenFlyerVocabGroupsView && (
        <LibraryQuizSessionRunner
          open={quizSessionOpen}
          onClose={() => setQuizSessionOpen(false)}
          exerciseType={partId}
          accentColor={style.color}
        />
      )}
    </div>
  );
};

export default LibraryPartDetailPage;
